/**
 * Remembers that this browser's network cannot carry the proxy's streamed responses.
 *
 * Some corporate networks (and HTTPS-scanning antivirus) hold an NDJSON response back
 * until the server ends it — or never deliver it at all. Streaming then buys nothing
 * and every turn pays the stream deadline before falling back. Once a stream fails in
 * a network-shaped way, both streamed paths (/turn/stream and /tts/stream) stay off
 * for this browser for a day, so later turns go straight to the legacy path.
 *
 * Only network-shaped failures of a stream that delivered nothing are recorded (see
 * the callers): the first-event deadline and fetch network errors. A stream that
 * delivered events proves the network can carry it, so its later stalls fail that
 * turn only. Server errors, HTTP statuses and 503 shedding say nothing about the
 * learner's network and never land here.
 *
 * Storage is best effort: private mode or blocked site data must never break a turn,
 * so every access is guarded and the module-level flag still covers this page load.
 */

const STORAGE_KEY = 'madameai_streaming_unhealthy';
export const STREAMING_UNHEALTHY_TTL_MS = 24 * 60 * 60 * 1000;

// Covers this page load even when localStorage is unavailable.
let unhealthyUntil = 0;

/**
 * Turn streaming off for this browser for STREAMING_UNHEALTHY_TTL_MS.
 * @param {string} reason - What failed (first_event_timeout, idle_timeout, ...).
 */
export function markStreamingUnhealthy(reason) {
  const until = Date.now() + STREAMING_UNHEALTHY_TTL_MS;
  unhealthyUntil = until;
  console.warn(`[Stream health] Streaming disabled for 24h on this browser: ${reason}`);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ reason, until }));
  } catch (error) {
    // Blocked storage: the module flag still covers this page load.
  }
}

/**
 * Whether the streamed paths may be tried. Expired entries are ignored.
 * @returns {boolean}
 */
export function isStreamingHealthy() {
  const now = Date.now();
  if (unhealthyUntil > now) return false;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return true;
    const { until } = JSON.parse(raw) || {};
    return !(Number.isFinite(until) && until > now);
  } catch (error) {
    // Unreadable or corrupt entry: fail open — the stream deadlines still protect the turn.
    return true;
  }
}

/** Test seam: forget the module-level flag (localStorage is the caller's to clear). */
export function resetStreamHealthForTests() {
  unhealthyUntil = 0;
}
